import React from 'react';
import { Table, Tooltip, Typography } from 'antd';
import type { TableColumnsType as ColumnsType } from 'antd';

import { shortDate } from '../../MarketExplorer/format';
import { FULL_COLOUR_Z, formatMove, moveUnitOf, zCellStyle } from './format';
import { Instrument, Turn, TurnMove } from './types';

type Props = {
  instruments: Instrument[];
  turns: Turn[];
};

const zText = (z: number | null) =>
  typeof z === 'number' && Number.isFinite(z) ? z.toFixed(2) : 'n/a';

const cellTooltip = (move: TurnMove) =>
  `z = ${zText(move.z)}${move.big_day ? ` · biggest day ${move.big_day}` : ''}`;

/**
 * Turns as columns, instruments as rows. Each cell is the turn's move in the
 * instrument's own unit (bp for a bond, % for every other kind), shaded by
 * |z| (full colour at |z| = 3; green up, red down) after the Benchmarks
 * heatmap. The table scrolls inside itself so a long window never widens the
 * page.
 */
const WeeklyGrid: React.FC<Props> = ({ instruments, turns }) => {
  if (!instruments.length || !turns.length) {
    return (
      <Typography.Text type="secondary">
        No weekly moves in this preview.
      </Typography.Text>
    );
  }
  const columns: ColumnsType<Instrument> = [
    {
      title: 'Instrument',
      key: 'instrument',
      fixed: 'left',
      width: 130,
      render: (_: unknown, instrument: Instrument) => (
        <span className="blueprint-builder__grid-instrument">
          <Typography.Text strong>{instrument.code}</Typography.Text>{' '}
          <Typography.Text type="secondary">
            {moveUnitOf(instrument)}
          </Typography.Text>
        </span>
      ),
    },
    ...turns.map(turn => ({
      title: (
        <Tooltip title={`${turn.start} → ${turn.end}`}>
          <span className="blueprint-builder__grid-turn">
            T{turn.index}
            <small>{shortDate(turn.end)}</small>
          </span>
        </Tooltip>
      ),
      key: `turn-${turn.index}`,
      width: 92,
      align: 'center' as const,
      onCell: (instrument: Instrument) => {
        const move = turn.moves[instrument.code];
        return { style: zCellStyle(move?.value, move?.z) };
      },
      render: (_: unknown, instrument: Instrument) => {
        const move = turn.moves[instrument.code];
        if (!move) return <Typography.Text type="secondary">—</Typography.Text>;
        return (
          <Tooltip title={cellTooltip(move)}>
            <span data-testid={`grid-${instrument.code}-T${turn.index}`}>
              {formatMove(move.value, move.unit || moveUnitOf(instrument))}
            </span>
          </Tooltip>
        );
      },
    })),
  ];
  return (
    <div className="blueprint-builder__grid">
      <Table<Instrument>
        size="small"
        bordered
        pagination={false}
        rowKey="code"
        columns={columns}
        dataSource={instruments}
        scroll={{ x: 'max-content' }}
      />
      <Typography.Text type="secondary" className="blueprint-builder__legend">
        Colour is the move's size against a normal week: full at |z| ={' '}
        {FULL_COLOUR_Z}; green up, red down. Hover a cell for z and the week's
        biggest day.
      </Typography.Text>
    </div>
  );
};

export default WeeklyGrid;
